/**
 * The navigation tree, built from application.json's FLAT `navigation` list by
 * `parent_id` — at ANY depth. No layout may assume two levels: a folder can sit
 * inside a folder inside a folder, and every helper here walks the whole chain.
 *
 * Only `"-"` folders and `"X"` websites may have children (see the backend's
 * `nav_validation`); anything else is a screen, even if a malformed menu hangs
 * entries under it — those entries stay reachable as its children here, exactly
 * as the classic tree has always shown them.
 */
import type { RunNavItem } from './types'

export interface NavTreeNode {
  item: RunNavItem
  children: NavTreeNode[]
  depth: number
  /** Ancestor folders, root first (never includes the node itself). */
  path: RunNavItem[]
  /** A folder (`-`), a website (`X`) or anything that has children. */
  isContainer: boolean
  isFolder: boolean
}

/** Guards every upward walk against a cyclic `parent_id` chain. */
const MAX_DEPTH = 64

export function sortByOrder(items: RunNavItem[]): RunNavItem[] {
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

export function getChildren(nav: RunNavItem[], parentId: string): RunNavItem[] {
  return sortByOrder(nav.filter((n) => (n.parent_id || '') === parentId))
}

/** The parent id the menu's roots hang from: `''`, or (legacy files) the application id. */
export function rootParentOf(nav: RunNavItem[], rootParentId?: string): string {
  return nav.some((n) => !n.parent_id) ? '' : (rootParentId || '')
}

export interface NavTree {
  roots: NavTreeNode[]
  byId: Map<string, NavTreeNode>
  /** Every screen (non-folder), in menu order (depth-first). */
  screens: NavTreeNode[]
}

export function buildNavTree(nav: RunNavItem[], rootParentId?: string): NavTree {
  const kids = new Map<string, RunNavItem[]>()
  for (const n of nav) {
    const pid = n.parent_id || ''
    const list = kids.get(pid)
    if (list) list.push(n)
    else kids.set(pid, [n])
  }
  for (const [k, v] of kids) kids.set(k, sortByOrder(v))
  const byId = new Map<string, NavTreeNode>()
  const screens: NavTreeNode[] = []
  const seen = new Set<string>()
  const build = (item: RunNavItem, depth: number, path: RunNavItem[]): NavTreeNode => {
    seen.add(item.id)
    const childItems = depth < MAX_DEPTH ? (kids.get(item.id) || []).filter((c) => !seen.has(c.id)) : []
    const isFolder = item.obj_type === '-'
    const node: NavTreeNode = {
      item, depth, path, isFolder,
      isContainer: isFolder || item.obj_type === 'X' || childItems.length > 0,
      children: [],
    }
    byId.set(item.id, node)
    if (!isFolder) screens.push(node)
    node.children = childItems.map((c) => build(c, depth + 1, [...path, item]))
    return node
  }
  const rootId = rootParentOf(nav, rootParentId)
  const roots = (kids.get(rootId) || []).map((r) => build(r, 0, []))
  return { roots, byId, screens }
}

/** Ancestor folders of `item`, root first. */
export function pathOf(nav: RunNavItem[] | Map<string, RunNavItem>, item: RunNavItem | null | undefined): RunNavItem[] {
  if (!item) return []
  const byId = nav instanceof Map ? nav : new Map(nav.map((n) => [n.id, n]))
  const out: RunNavItem[] = []
  const seen = new Set<string>([item.id])
  let cur = item.parent_id ? byId.get(item.parent_id) : undefined
  while (cur && !seen.has(cur.id) && out.length < MAX_DEPTH) {
    out.unshift(cur)
    seen.add(cur.id)
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined
  }
  return out
}

/** "Ticket Booking & Sales › After Sales › Printing" — the folders a screen sits in. */
export function pathText(path: RunNavItem[], sep = ' › '): string {
  return path.map((p) => p.title).filter(Boolean).join(sep)
}

/**
 * Resolve a screen by object name: exact first, case-insensitively as a
 * fallback (object names are case-insensitive identifiers in practice — a link
 * built from a model file can differ in case from the menu entry).
 */
export function findScreen(nav: RunNavItem[], objName: string | null | undefined): RunNavItem | null {
  const target = (objName || '').trim()
  if (!target) return null
  return nav.find((n) => n.obj_name === target && n.obj_type !== '-')
    || nav.find((n) => n.obj_type !== '-' && (n.obj_name || '').toLowerCase() === target.toLowerCase())
    || null
}

/**
 * The entry the application opens on: the FIRST screen of the menu — the first
 * top-level item, or the first item inside the first folder (at any depth) —
 * and only when that screen is "Home" (object name, else title).
 */
export function findHome(nav: RunNavItem[], rootParentId?: string): RunNavItem | null {
  const roots = getChildren(nav, rootParentOf(nav, rootParentId))
  const first = (items: RunNavItem[], guard = 0): RunNavItem | null => {
    for (const it of items) {
      if (it.obj_type !== '-') return it
      const inside = guard < MAX_DEPTH ? first(getChildren(nav, it.id), guard + 1) : null
      if (inside) return inside
    }
    return null
  }
  const top = first(roots)
  if (!top) return null
  const isHome = (v?: string) => (v || '').trim().toLowerCase() === 'home'
  return isHome(top.obj_name) || isHome(top.title) ? top : null
}

/** Ids of every ancestor folder of `item` (for expanding a tree to it). */
export function ancestorIds(nav: RunNavItem[], item: RunNavItem): string[] {
  return pathOf(nav, item).map((p) => p.id)
}
